import { type SelectOption, type Slider } from '@solinkcorp/solink-components';
import {
  type FormikComputedProps,
  type FormikErrors,
  type FormikHandlers,
  type FormikState,
  type FormikTouched,
} from 'formik';
import every from 'lodash/every';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import type React from 'react';
import { useCallback, useRef } from 'react';

export type FormikObj<T> = FormikState<T> &
  FormikComputedProps<T> &
  FormikHandlers & {
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    setFieldTouched: (field: string, isTouched?: boolean, shouldValidate?: boolean) => void;
    setValues: (values: any, shouldValidate?: boolean) => void;
  };

/**
 * Filters a Formik `errors` object to only include fields that are either
 * dirty, or whose value differs from `initialValues`.  Note that this only
 * works if Formik errors are strings!
 */
export function getTouchedErrors<T>({
  errors = {},
  initialValues,
  touched,
  values,
}: {
  errors?: FormikErrors<T>;
  initialValues: T;
  touched: FormikTouched<T>;
  values: T;
}): FormikErrors<T> {
  const filteredErrors: FormikErrors<T> = {};

  for (const [k, err] of Object.entries(errors)) {
    const key = k as keyof T;
    if (typeof err === 'string') {
      // This is an error.
      if (touched[key] || initialValues[key] !== values[key]) {
        (filteredErrors as any)[key] = err;
      }
    } else {
      // This is an object full of nested properties.
      (filteredErrors as any)[key] = getTouchedErrors({
        errors: (err as FormikErrors<any> | undefined) || {},
        initialValues: initialValues[key] || {},
        touched: (touched[key] as FormikTouched<any>) || {},
        values: values[key] || {},
      });
    }
  }

  return filteredErrors;
}

/**
 * Returns a list of fixed <Select /> values
 * @param  options  The current values of each field
 * @return         Returns an array of values (or an empty array)
 */
export function getFixedValues(options: SelectOption[]): string[] {
  const fixedValues: string[] = [];
  options.forEach((option) => {
    if (option.isFixed) {
      fixedValues.push(option.value);
    }
  });
  return fixedValues;
}

/**
 * Used in conjunction with a Select input from @solinkcorp/components, this is
 * used to ensure that when a user opens a menu, the menu will be scrolled into
 * view.
 *
 * @param  {Object} wrapper The element to scroll to try to move the menu into view.
 */
export function scrollMenuIntoView(wrapper: Element | undefined | null) {
  return setTimeout(() => {
    const menu = document.querySelector('.select__menu');
    if (menu && wrapper && wrapper.parentElement) {
      const parentPosition = wrapper.parentElement.getBoundingClientRect();
      const position = menu.getBoundingClientRect();
      if (parentPosition.bottom < position.bottom) {
        if (menu?.className.includes('select__menu--customized-')) {
          scrollCustomMenuIntoView(menu, wrapper);
        } else {
          menu.scrollIntoView();
        }
      }
    }
  });
}

/**
 * Used in conjunction with a customized select input from WidgetLocationsSelect, this is
 * used to ensure that when a user opens a customized popup menu, the menu will be scrolled into
 * view.
 * The select control has class name `select__control--customized-${id}`
 * The select menu has class name `select__menu--customized-${id}`
 * @param menu The menu
 * @param wrapper The element to scroll to try to move the menu into view.
 */
function scrollCustomMenuIntoView(menu: Element, wrapper: Element) {
  const id = menu.className.match(/select__menu--customized-(.*)/)?.[1];
  const customizedControl = document.querySelector(
    `.select__control--customized-${id}`,
  ) as HTMLElement;
  if (customizedControl) {
    // if after scrolling the menu into view, the menu is still cut off, then
    // add some extra space to the bottom of the wrapper element
    if (wrapper.parentElement) {
      const moreSpaceTheMenuNeedAfterScroll =
        menu.getBoundingClientRect().height -
        (wrapper.getBoundingClientRect().bottom - customizedControl.getBoundingClientRect().bottom);
      if (moreSpaceTheMenuNeedAfterScroll > 0) {
        const paddingTop = parseInt(window.getComputedStyle(wrapper.parentElement).paddingTop, 10);
        wrapper.parentElement.style.paddingBottom = `${
          paddingTop + moreSpaceTheMenuNeedAfterScroll
        }px`;
      }
    }

    // scroll the control to the top of the form
    wrapper.parentElement?.scroll({ top: customizedControl.offsetTop - 5 });
    const newViewportOffset = customizedControl.getBoundingClientRect();
    // move the menu to the bottom of the control
    (menu as HTMLElement).style.top = `${newViewportOffset.bottom + 5}px`;
  }
}

/**
 * If you have one or more Select elements inside a scrollable container, you can
 * use this to ensure that when a Select menu is opened, it will be scrolled into
 * view.  To use:
 *
 *     const scrollMenuIntoView = useScrollMenuIntoView();
 *     ...
 *     <div {...scrollMenuIntoView}>
 *
 * Where `div` is your scrollable container.
 */
export function useScrollMenuIntoView(): {
  onClick: () => void;
  onKeyDown: () => void;
  ref: React.LegacyRef<HTMLElement>;
} {
  const ref = useRef<null | HTMLElement>(null);
  const doScroll = useCallback(() => scrollMenuIntoView(ref.current), []);

  return {
    onClick: doScroll,
    onKeyDown: doScroll,
    ref,
  };
}

/**
 * Verifies if the user can save a form, based on its dirty and error props
 * @param  dirty  Whether the form has been changed
 * @param  errors Whether there are any errors in the form
 * @param  validateOnMount Whether the form is validated on mount
 * @return whether the user can save the form or not
 */
export function canSave({
  dirty,
  errors,
  validateOnMount,
}: {
  dirty: boolean;
  errors: FormikErrors<any>;
  validateOnMount?: boolean;
}) {
  const areErrorsEmpty = isEmpty(errors) || every(errors, isEmpty);
  return areErrorsEmpty && (dirty || (!dirty && validateOnMount));
}

function getFormikError<T>(
  formik: FormikState<T> & FormikComputedProps<T>,
  field: string,
  validateBeforeTouched?: boolean,
): string | undefined {
  const { errors, touched, values, initialValues } = formik;
  const error = get(errors, field);
  const isTouched = error && get(touched, field);
  const isModified = error && !isEqual(get(values, field), get(initialValues, field));
  return isTouched || isModified || validateBeforeTouched ? error : undefined;
}

export interface FieldPropsResult<H, V> {
  name: string;
  errorText?: string;
  value: string | undefined;
  changeByProp: string;
  onChange: (changeByProp: string, value: V) => void;
  onBlur: React.FocusEventHandler<H>;
}

function fieldPropsHelper<T, H, V>(
  formik: FormikObj<T>,
  field: string,
  validateBeforeTouched?: boolean,
): FieldPropsResult<H, V> {
  return {
    name: field,
    errorText: getFormikError(formik, field, validateBeforeTouched),
    value: get(formik.values, field),
    changeByProp: field,
    onChange: formik.setFieldValue,
    onBlur: formik.handleBlur,
  };
}

type HTMLInputs = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

/**
 * Generate props to pass to a @solinkcorp/solink-components#TextField, TextArea,
 * or Select control.
 */
export function fieldProps<T, H extends HTMLInputs>(
  formik: FormikObj<T>,
  field: string,
  validateBeforeTouched?: boolean,
): {
  name: string;
  errorText?: string;
  value: string | undefined;
  changeByProp: string;
  onChange: (changeByProp: string, value: string) => void;
  onBlur: React.FocusEventHandler<H>;
} {
  return fieldPropsHelper(formik, field, validateBeforeTouched);
}

/**
 * Generate props to pass to a @solinkcorp/solink-components#NumberField.
 */
export function numberFieldProps<T>(
  formik: FormikObj<T>,
  field: string,
): FieldPropsResult<HTMLInputElement, number> {
  return fieldPropsHelper(formik, field);
}

/**
 * Generate props to pass to a @solinkcorp/solink-components#Checkbox.
 */
export function checkboxProps<T>(
  formik: FormikObj<T>,
  field: string,
): {
  name: string;
  errorText?: string;
  checked: boolean;
  changeByProp: string;
  onChange: (changeByProp: string, value: boolean) => void;
  onBlur: React.FocusEventHandler<HTMLInputElement>;
} {
  return {
    name: field,
    errorText: getFormikError(formik, field),
    checked: get(formik.values, field),
    changeByProp: field,
    onChange: formik.setFieldValue,
    onBlur: formik.handleBlur,
  };
}

export function radioGroupProps<T>(
  formik: FormikObj<T>,
  field: string,
): {
  name: string;
  errorText?: string;
  checkedValue: string | number | undefined;
  changeByProp: string;
  onChange: (changeByProp: string, value: string | number) => void;
  onBlur: React.FocusEventHandler<HTMLInputElement>;
} {
  return {
    name: field,
    errorText: getFormikError(formik, field),
    checkedValue: get(formik.values, field),
    changeByProp: field,
    onChange: formik.setFieldValue,
    onBlur: formik.handleBlur,
  };
}

export function sliderProps<T>(
  formik: FormikObj<T>,
  field: string,
): {
  onChange: React.ComponentProps<typeof Slider>['onChange'];
  value: React.ComponentProps<typeof Slider>['value'];
} {
  return {
    onChange: (value) => formik.setFieldValue(field, value),
    value: get(formik.values, field),
  };
}

export function isNumberOnly(value: string) {
  return /^\d+$/.test(value);
}
